import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { VendorTaxCertificationPage } from './VendorTaxCertificationPage';
import { getVendorDocuments } from 'store/vendorStore/vendorDocuments/dispatch';
import { getVendorCommentsData } from 'store/vendorStore/vendorComments/dispatch';
import { updateEtaTaxCertification } from 'store/vendorStore/vendorTaxCertificationData/dispatch';
import {
  getTaxCertificationRequestsAsyncAction,
  sendToSpruceAsyncAction,
} from 'store/vendorStore/vendorTaxCertificationData/slices';
import { useAppDispatch } from 'store/store';
import { useHistory, useParams } from 'react-router-dom';
import { eVendorServiceType } from '../../types/order/title/eVendorServiceType';
import { IVendorDocuments } from '../../components/vendorDashboard/StatusSection';
import { getTaxCertificationAsyncAction } from '../../store/order/taxCertifications/taxCertificationSlice';
import { ClientUrlFactory } from 'ui-toolkit/rest/ClientUrlFactory';
import { UUID } from '../../types/shared/UUID';
import { VendorWrapper } from '../../components/vendorDashboard/VendorWrapper';
import { VendorSelectors } from '../../selectors/vendorPortal/VendorSelectors';
import { findError } from '../../utilities/vendorDashboard/findError';
import { isLoading } from '../../utilities/vendorDashboard/isLoading';
import { isTaxCertificateName } from '../../types/order/documents/IDocumentCategoryType';

interface IVendorTaxCertificationContainerProps {}

export const VendorTaxCertificationContainer: React.FC<IVendorTaxCertificationContainerProps> = () => {
  const { orderId } = useParams<{ orderId: UUID }>();

  const dispatch = useAppDispatch();

  const {
    updateVendorTaxCertificationSlice: { fetchError: updateTaxCertError, fetchStatus: updateTaxCertFetchStatus },
    taxCertificationRequestsSlice: { data: taxCertRequest, fetchError: taxCertError, fetchStatus: taxCertFetchStatus },
    sendToSpruceSlice: { fetchError: sentToSpruceError, fetchStatus: sentToSpruceFetchStatus },
    taxCertificationSlice: taxCertificationList,
  } = useSelector(VendorSelectors.selectTaxCertification);

  const { documents, documentsError, uploadDocumentsFetchStatus } = useSelector(VendorSelectors.selectDocuments);
  const { getCommentsFetchStatus, getCommentsError, postCommentError } = useSelector(VendorSelectors.selectComments);

  const history = useHistory();

  const pageError = useMemo(() => {
    return findError(
      taxCertError?.message,
      documentsError,
      getCommentsError,
      postCommentError,
      sentToSpruceError?.message,
      updateTaxCertError?.message
    );
  }, [taxCertError, documentsError, getCommentsError, postCommentError, sentToSpruceError, updateTaxCertError]);

  const pageLoading = useMemo(() => {
    return isLoading(taxCertFetchStatus.fetching, getCommentsFetchStatus.fetching, sentToSpruceFetchStatus.fetching);
  }, [taxCertFetchStatus, getCommentsFetchStatus, sentToSpruceFetchStatus]);

  useEffect(() => {
    dispatch(getTaxCertificationRequestsAsyncAction(orderId));
    dispatch(getVendorDocuments(orderId));
    dispatch(getVendorCommentsData(orderId, [eVendorServiceType.taxCertificationRequest]));
  }, [dispatch, orderId]);

  const requiredDocuments: IVendorDocuments[] = [
    {
      label: 'Tax Certificate Complete',
      document: documents[orderId] ? documents[orderId].find((doc) => isTaxCertificateName(doc.category)) : undefined,
    },
  ];

  const readyToSend = (): boolean => {
    return requiredDocuments.every((d) => d.document);
  };

  const onUpdateEta = (value?: string | null) =>
    dispatch(updateEtaTaxCertification(orderId, taxCertRequest.taxCertificationRequestId, value));

  useEffect(() => {
    dispatch(getTaxCertificationRequestsAsyncAction(orderId));
    dispatch(getTaxCertificationAsyncAction(orderId));
  }, [dispatch, orderId]);

  const existingTaxCertRequestId = taxCertRequest.taxCertificationRequestId;

  const onSendTaxCertToSpruce = async () => {
    if (existingTaxCertRequestId) {
      await dispatch(sendToSpruceAsyncAction(existingTaxCertRequestId));
    }
    window.setTimeout(() => history.push(ClientUrlFactory().urlFor.vendorTaxCertifications()), 3000);
  };

  const existingTaxCert =
    taxCertificationList.data.taxCertifications.length > 0 ? taxCertificationList.data.taxCertifications[0] : undefined;

  return (
    <VendorWrapper isLoading={pageLoading}>
      <VendorTaxCertificationPage
        orderId={orderId}
        taxCertRequest={taxCertRequest}
        pageError={pageError}
        documents={documents[orderId] || []}
        requiredDocuments={requiredDocuments}
        uploadDocumentFetch={uploadDocumentsFetchStatus}
        onSendToSpruce={onSendTaxCertToSpruce}
        readyToSend={readyToSend()}
        sendToSpruceFetchStatus={sentToSpruceFetchStatus}
        sendToSpruceError={sentToSpruceError}
        onUpdateEta={onUpdateEta}
        updateEtaFetch={updateTaxCertFetchStatus}
        taxCert={existingTaxCert}
        onSendTaxCertToSpruce={onSendTaxCertToSpruce}
      />
    </VendorWrapper>
  );
};
